import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Download, Loader2, Truck, Package } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const { user } = useAuth();
  const guestEmail = localStorage.getItem("hoh_guest_email");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order); setStatus("paid"); clearCart();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const downloadItem = async (item) => {
    try {
      const params = !user ? { email: guestEmail } : {};
      const { data } = await api.get(`/download/${order.id}/${item.product_id}/${item.format}`, { params });
      if (data.download_url) window.open(data.download_url, "_blank");
    } catch (e) { console.error(e); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="checkout-success">
      {status === "polling" && (
        <div>
          <Loader2 size={48} className="animate-spin text-[#D4AF37] mx-auto" />
          <h1 className="font-serif-display text-3xl text-white mt-6">Confirming your payment...</h1>
          <p className="text-neutral-400 mt-3">Please don't close this page.</p>
        </div>
      )}
      {status === "paid" && order && (
        <div className="text-left">
          <div className="text-center">
            <CheckCircle2 size={64} className="text-[#D4AF37] mx-auto" />
            <h1 className="font-serif-display text-5xl text-white mt-6">Thank you!</h1>
            <p className="text-neutral-300 mt-3">Your purchase is confirmed.</p>
          </div>

          {/* Digital downloads */}
          {order.items.filter((i) => i.type !== "merch").length > 0 && (
            <div className="luxury-card mt-12 rounded-xl p-6">
              <div className="label-eyebrow mb-4 flex items-center gap-2"><Download size={14} /> Digital Downloads</div>
              <div className="space-y-3">
                {order.items.filter((i) => i.type !== "merch").map((i) => (
                  <div key={`${i.product_id}-${i.format}`} className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0">
                    <div>
                      <div className="text-white">{i.title}</div>
                      <div className="text-xs tracking-widest uppercase text-neutral-500">{i.format} · £{Number(i.price).toFixed(2)}</div>
                    </div>
                    <button data-testid={`download-${i.product_id}-${i.format}`} onClick={() => downloadItem(i)} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
                      <Download size={14} /> Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Merch shipping */}
          {order.items.filter((i) => i.type === "merch").length > 0 && (
            <div className="luxury-card mt-6 rounded-xl p-6">
              <div className="label-eyebrow mb-4 flex items-center gap-2"><Truck size={14} /> Merchandise · Shipping</div>
              <div className="text-sm text-neutral-300 mb-4">Status: <span className="text-[#D4AF37] uppercase tracking-widest">{order.shipping_status || "pending"}</span></div>
              <div className="space-y-3">
                {order.items.filter((i) => i.type === "merch").map((i) => (
                  <div key={`${i.product_id}-${i.size}-${i.color}`} className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0">
                    <div>
                      <div className="text-white">{i.title} <span className="text-neutral-400 text-xs">× {i.quantity || 1}</span></div>
                      <div className="text-xs tracking-widest uppercase text-neutral-500">
                        {i.size && `${i.size}`}{i.color && ` · ${i.color}`}
                      </div>
                    </div>
                    <Package size={18} className="text-[#D4AF37]" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500 mt-4">A confirmation email with tracking will follow once your order ships.</p>
            </div>
          )}

          <div className="text-center mt-8">
            <div className="text-xs tracking-widest uppercase text-neutral-500">Order #{order.id.slice(0, 8)}</div>
            {user && <Link to="/downloads" className="mt-6 inline-block text-[#D4AF37] hover:text-[#FCE68A]">View all my orders →</Link>}
          </div>
        </div>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <div>
          <h1 className="font-serif-display text-3xl text-white">We couldn't confirm your payment.</h1>
          <p className="text-neutral-400 mt-3">If you were charged, please contact support.</p>
          <Link to="/" className="btn-gold mt-6 inline-block px-6 py-3 rounded text-xs tracking-widest uppercase">Back home</Link>
        </div>
      )}
    </div>
  );
}
